import { Component, Inject, ChangeDetectorRef, OnDestroy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { DialogComponentBase } from '@wephone-core-ui';
import {
  IFlexDialogConfig,
  DialogActionButton,
  Colors,
  NoWhitespaceValidator,
  AsciiValidator,
  DialogService,
  SmsMaxValidator,
  SmsMaxLength,
} from '@wephone-utils';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DidRepository } from '@wephone-core/model/repository/did';
import { DidEntity } from '@wephone-core/model/entity/did';
import { _tk, _ti } from '@wephone-translation';
import { SentSmsResult, SendStatus, SmsService, SmsMessageCount } from '../../service/sms.service';
import { UserProfileService, CallingNumber } from '@wephone/services/user-profile.service';
import { Deferred } from 'ts-deferred';
import { NotPhoneNumberValidated, PhoneNumberValidated } from '@wephone/services/form-validator';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import { EnterpriseRepository } from '@wephone-core/model/repository/enterprise';
import { EnterpriseEntity } from '@wephone-core/model/entity/enterprise';
import { SmsTemplateEntity } from '@wephone-core/model/entity/sms_template';
import { SmsTemplateRepository } from '@wephone-core/model/repository/sms_template';
import { SmsSetScheduleComponent } from '../sms-set-schedule/sms-set-schedule.component';
import { ValidatorService } from '@wephone-utils/services/validator.service';

export interface ISentSmsModalData {
  caller?: string;
  destination?: string;
}

@Component({
  selector: 'sent-sms-modal',
  templateUrl: './sent-sms-modal.html',
  styleUrls: ['./sent-sms-modal.scss']
})
export class SentSmsModalComponent extends DialogComponentBase implements OnDestroy {
  static modalConfig: IFlexDialogConfig = {
    size: 's',
    width: '400px',
    minHeight: '90%',
  };

  static SenderNameValidator = [
    Validators.pattern(ValidatorService.VALID_SMS_SENDER_NAME_PATTERN),
    Validators.maxLength(11), NoWhitespaceValidator, AsciiValidator, NotPhoneNumberValidated()
  ];

  dialogTitle = _tk('sms.send_sms');

  dialogLeftActions: DialogActionButton[] = [
    {
      label: _tk('sms.send_scheduled'),
      action: () => {
        this.sendScheduled();
      },
      visible: () => {
        return true;
      },
      icon: 'schedule_send',
      color: Colors.PRIMARY,
    }
  ];

  dialogRightActions: DialogActionButton[] = [
    {
      label: _tk('sms.send'),
      action: () => {
        this.submit();
      },
      visible: () => {
        return true;
      },
      icon: 'send',
      color: Colors.PRIMARY,
    }
  ];

  sentSmsForm: FormGroup;
  didOptionList: DidEntity[] = [];
  smsTemplateList: SmsTemplateEntity[];
  smsContentInfo: SmsMessageCount;
  smsMaxLength: number = SmsMaxLength;

  private defaultSender: string;
  private senderNameDefault: string;
  private readonly didRepo: DidRepository = DidRepository.getInstance<DidRepository>();

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: ISentSmsModalData,
    private readonly fb: FormBuilder,
    dialogRef: MatDialogRef<SentSmsModalComponent>,
    private readonly smsService: SmsService,
    private readonly profileService: UserProfileService,
    private readonly dialogService: DialogService,
    cdRef: ChangeDetectorRef,
  ) {
    super(dialogRef, cdRef);
  }

  protected async resolveData(): Promise<void> {
    await this.initDidOptionList();
    this.smsTemplateList = await SmsTemplateRepository.getInstance<SmsTemplateRepository>().findAll(false);
    const myEnterprise: EnterpriseEntity = EnterpriseRepository.getInstance<EnterpriseRepository>().getMyEnterprise();
    this.senderNameDefault = myEnterprise && myEnterprise.name || '';

    this.defaultSender = this.data.caller;
    this.initFormGroup();
    this.updateDialogLayout();
  }

  initFormGroup(): void {
    this.sentSmsForm = this.fb.group({
      sender: [this.defaultSender],
      sms_template: [],
      sender_name: [this.senderNameDefault, SentSmsModalComponent.SenderNameValidator],
      destination: [this.data.destination, [Validators.required, Validators.maxLength(20), PhoneNumberValidated()]],
      message: ['', {
        validators: [Validators.required, NoWhitespaceValidator],
        updateOn: 'change'
      }]
    });

    this.setSmsContentInfo();
    this.setValidatorsSmsMessage();

    this.addSubscription(
      this.sentSmsForm.get('sms_template').valueChanges.subscribe((smsTemplate: SmsTemplateEntity) => {
        this.setFormControlValue('sender_name', smsTemplate && smsTemplate.sender_name);
        this.setFormControlValue('message', smsTemplate && smsTemplate.content || null);
      })
    );

    this.addSubscription(
      this.sentSmsForm.get('sender_name').valueChanges.subscribe(() => {
        this.setValidatorsSmsMessage();
      })
    );

    this.addSubscription(
      this.sentSmsForm.get('message').valueChanges.subscribe(() => {
        this.setSmsContentInfo();
      })
    );
  }

  hasLimitedSms(): boolean {
    return !!this.sentSmsForm.get('sender_name').value;
  }

  private setSmsContentInfo(): void {
    this.smsContentInfo = this.smsService.getSmsMessageCount(this.sentSmsForm.get('message').value);
    this.updateDialogLayout();
  }

  private setValidatorsSmsMessage(): void {
    if (this.sentSmsForm.get('sender_name').value) {
      this.sentSmsForm.get('message').setValidators([Validators.required, SmsMaxValidator, NoWhitespaceValidator]);
    } else {
      this.sentSmsForm.get('message').setValidators([Validators.required, NoWhitespaceValidator]);
    }
  }

  private setFormControlValue(controlName: string, value?: any): void {
    this.sentSmsForm.get(controlName).setValue(value);
    this.sentSmsForm.get(controlName).markAsDirty();
  }

  handleKeyboardEvent(event: KeyboardEvent): void {
    // this.loggerService.log('key event trigger');
    event.stopPropagation();
  }

  sendScheduled(): Promise<void> {
    return this.submit(true);
  }

  async submit(schedule: boolean = false): Promise<void> {
    this.sentSmsForm.get('sender').setErrors(null);

    const senderNameForm = this.sentSmsForm.get('sender_name');
    senderNameForm.setErrors(null);

    senderNameForm.setValidators(SentSmsModalComponent.SenderNameValidator);
    senderNameForm.updateValueAndValidity();

    if (!this.sentSmsForm.get('sender').value && (
      !senderNameForm.value ||
      !senderNameForm.value.trim()
    )) {
      this.sentSmsForm.get('sender').setErrors({
        sender_required: true
      });

      senderNameForm.setErrors({
        sender_required: true
      });
    }

    if (this.sentSmsForm.invalid) {
      const messageCtrl = this.sentSmsForm.get('message');
      const message = messageCtrl.value;
      if (!_.trim(message)) {
        messageCtrl.setValue('');
      }

      this.sentSmsForm.markAllAsTouched();
      this.toastService.showError(_ti('public.message.data_invalid'));
      return;
    }

    try {
      if (!schedule) {
        await this.sendSms();
      } else {
        this.dialogService.openDialog2(SmsSetScheduleComponent, {

        }, async (scheduledDate: DateTime) => {
          if (!scheduledDate) {
            return;
          }

          await this.sendSms(scheduledDate);
        });
      }

    } catch (error) {
      const msg = _ti('sms.alert.send_failure');
      this.toastService.showErrorMessage(error, msg);
    }
  }

  async sendSms(scheduleDate: DateTime = null): Promise<void> {
    const formValue = this.sentSmsForm.value;
    const smsSent: {
      sender: string,
      sender_name: string,
      destination: string,
      message: string
    } = {
      sender: formValue.sender,
      sender_name: formValue.sender_name,
      destination: formValue.destination,
      message: formValue.message
    };

    try {
      const ret: SentSmsResult[] = await this.smsService.sendSms(
        smsSent.sender,
        smsSent.destination,
        _.trim(smsSent.message),
        smsSent.sender_name || null,
        scheduleDate
      );

      const smsMessage: SentSmsResult = ret && ret.length ? ret[0] : undefined;

      if (!scheduleDate) {
        if (smsMessage && smsMessage.sms &&
          !_.includes([SendStatus.SEND_STATUS_SENT, SendStatus.SEND_STATUS_DELIVERED], smsMessage.sms.send_status)
        ) {
          this.showWarning(_ti('sms.alert.send_waiting'));
        } else {
          this.successToast(_ti('sms.alert.send_success'));
        }
      } else {
        this.successToast(_ti('sms.alert.schedule_success'));
      }

      this.dismiss(ret);
    } catch (error) {
      this.showErrorMessage(error, scheduleDate ? _ti('sms.alert.schedule_failure') : _ti('sms.alert.send_failure'));
    }
  }

  cancel(result?): void {
    this.dismiss(result);
  }

  private async initDidOptionList(): Promise<void> {
    const ret = new Deferred<any>();
    try {
      const callingNumbers: CallingNumber[] = await this.profileService.getCallingNumbers();
      if (!callingNumbers) {
        this.loggerService.warn('No user numbers');
        throw new Error('No user numbers');
      }
      const didList = await this.didRepo.loadObjectList();
      this.didOptionList = didList.filter((d: DidEntity) => {
        return !!callingNumbers.find(x => x.id === d.id && x.sms === 1);
      });

      // if (!this.didOptionList.length) {
      //   this.loggerService.error('No number can send SMS');
      //   throw new Error('No number can send SMS');
      // }

      if (this.defaultSender && callingNumbers.find(x => x.number === this.defaultSender && x.sms === 1)) {
        ret.resolve();
        return;
      }

      if (!this.defaultSender) {
        this.defaultSender = this.didOptionList && this.didOptionList[0] && this.didOptionList[0].number;
      }

      ret.resolve();
    } catch (error) {
      this.loggerService.error('Init DID option list error', error);
      ret.reject();
    }

    return ret.promise;
  }

  clearControl(controlName: string, e: MouseEvent): void {
    e.stopPropagation();
    this.setFormControlValue(controlName, null);
  }
}
